import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import { EnsoAnimated, FujiLines, BrushDivider, PathLine } from '../components/Motifs';

const SUMI_BRUSH = '/images/sumi-brush.png';
const FUJI_INDIGO = '/images/fuji-indigo.png';
const TRAIL_FOREST = '/images/trail-forest.jpg';

const Home = () => {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ['start start', 'end start'],
    });
    const brushY = useTransform(scrollYProgress, [0, 1], [0, 120]);
    const kanjiY = useTransform(scrollYProgress, [0, 1], [0, -80]);
    const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    useEffect(() => {
        document.title = 'Nakasendo Capital — Your Companion in Value Creation';
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section
                ref={heroRef}
                className="relative min-h-[92vh] overflow-hidden flex items-center"
                aria-label="Hero"
            >
                {/* Background sumi brush watermark */}
                <motion.img
                    src={SUMI_BRUSH}
                    alt=""
                    aria-hidden="true"
                    style={{ y: brushY }}
                    className="absolute -top-20 -right-20 w-[80%] md:w-[60%] h-auto opacity-[0.18] pointer-events-none select-none mix-blend-multiply"
                />
                {/* Kanji watermark */}
                <motion.div
                    aria-hidden="true"
                    style={{ y: kanjiY, color: 'rgba(48, 58, 82, 0.05)' }}
                    className="absolute -left-10 md:left-0 -top-4 md:-top-10 font-jp text-[28vw] md:text-[20vw] leading-none pointer-events-none select-none whitespace-nowrap font-light"
                >
                    中山道
                </motion.div>

                <motion.div
                    style={{ opacity: heroOpacity }}
                    className="relative z-10 px-6 md:px-12 lg:px-24 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-end pt-12"
                >
                    <div className="lg:col-span-9">
                        <Reveal delay={0.1}>
                            <p className="overline">Boutique Advisory · Asia</p>
                        </Reveal>
                        <Reveal delay={0.25}>
                            <h1 className="h1-editorial text-[clamp(3rem,9vw,9rem)] mt-8 max-w-[18ch]">
                                Your Companion <br />in <em>Value Creation.</em>
                            </h1>
                        </Reveal>
                        <Reveal delay={0.5}>
                            <div className="mt-12 max-w-2xl">
                                <BrushDivider className="w-32 h-3 mb-6" color="#1A1A1A" />
                                <p className="font-sans text-lg md:text-xl leading-relaxed text-ink-soft font-light">
                                    We help ambitious companies build partnerships to grow across Asia,
                                    execute successful transactions, and shape their equity story.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.7}>
                            <div className="mt-12 flex flex-wrap gap-4 md:gap-6">
                                <Link to="/contact" data-testid="hero-cta-work" className="btn-primary">
                                    Work With Us <span className="arrow">→</span>
                                </Link>
                                <Link to="/services" data-testid="hero-cta-services" className="btn-ghost">
                                    Discover Services <span className="arrow">→</span>
                                </Link>
                            </div>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-3 hidden lg:flex flex-col items-end gap-6 self-end pb-8">
                        <Reveal delay={0.9}>
                            <div className="flex flex-col items-end text-right">
                                <p className="overline text-ink-soft">Est.</p>
                                <p className="font-serif text-5xl mt-1">2025</p>
                            </div>
                        </Reveal>
                        <Reveal delay={1.0}>
                            <div className="vertical-text overline">Scroll · 中山道 · Journey</div>
                        </Reveal>
                    </div>
                </motion.div>

                {/* Bottom indicator */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2, duration: 0.8 }}
                    className="absolute bottom-8 left-6 md:left-12 lg:left-24 flex items-center gap-3 text-ink-soft"
                >
                    <ArrowDown size={14} strokeWidth={1} />
                    <span className="overline">Begin</span>
                </motion.div>
            </section>

            {/* NARRATIVE — Inspired by the Road */}
            <section
                aria-label="Inspired by the Road of Growth"
                className="relative px-6 md:px-12 lg:px-24 py-24 lg:py-40 border-t border-ink/10 overflow-hidden"
            >
                {/* Path line decoration */}
                <PathLine className="absolute -right-24 top-0 h-full w-[260px] text-aizome opacity-30 hidden lg:block" stroke="#303a52" />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <div className="lg:col-span-6 order-2 lg:order-1">
                        <Reveal>
                            <p className="overline">— 01 / The Inspiration</p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                                Inspired by the<br />
                                <em className="italic font-light">Road of Growth.</em>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <div className="mt-10 space-y-6 max-w-xl text-ink-soft font-light text-base md:text-lg leading-relaxed">
                                <p>
                                    <span className="font-jp text-ink not-italic">中山道</span> — the
                                    <em className="font-serif text-ink"> Nakasendō </em>
                                    — was a historic road connecting two Japanese capitals,
                                    travelled for centuries by leaders, merchants, and innovators.
                                </p>
                                <p>
                                    Inspired by that path, we walk alongside our clients to define,
                                    shape, and execute their growth journey — from equity story to
                                    transactions and partnerships that build lasting value.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.45}>
                            <Link to="/about" data-testid="home-link-about" className="btn-ghost mt-12">
                                Our Story <span className="arrow">→</span>
                            </Link>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-6 order-1 lg:order-2 relative">
                        <Reveal delay={0.2} y={40}>
                            <div className="img-frame border border-ink/10">
                                <img
                                    src={TRAIL_FOREST}
                                    alt="Nakasendo trail through ancient cedar forest"
                                    className="w-full aspect-[4/5] object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </Reveal>
                        {/* Floating caption */}
                        <Reveal delay={0.5}>
                            <div className="absolute -bottom-6 -left-4 md:-left-10 bg-ivory border border-ink/15 px-5 py-3">
                                <p className="overline text-[10px]">Kiso Valley · 木曽路</p>
                            </div>
                        </Reveal>
                        <EnsoAnimated
                            className="absolute -top-10 -right-6 w-24 h-24 text-aizome opacity-50"
                            stroke="#303a52"
                            size={120}
                        />
                    </div>
                </div>
            </section>

            {/* SERVICES OVERVIEW */}
            <section
                aria-label="Services overview"
                className="relative px-6 md:px-12 lg:px-24 py-24 lg:py-32 border-t border-ink/10"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 lg:mb-24">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="overline">— 02 / What We Do</p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                                Three pillars,<br />
                                <em className="italic font-light">one journey.</em>
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                        <Reveal delay={0.3}>
                            <p className="text-ink-soft text-base md:text-lg leading-relaxed font-light max-w-lg">
                                Our expertise spans three complementary disciplines —
                                designed to support you through each stage of your value creation journey.
                            </p>
                        </Reveal>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border-y border-ink/10">
                    {[
                        {
                            num: '01',
                            title: 'Partnerships',
                            jp: '提携',
                            desc: 'Building bridges across markets and industries — strategic collaborations and joint ventures across Asia.',
                        },
                        {
                            num: '02',
                            title: 'Transactions',
                            jp: '取引',
                            desc: 'Buy-side and sell-side advisory for private assets, project portfolios, and infrastructure.',
                        },
                        {
                            num: '03',
                            title: 'Equity Story',
                            jp: '物語',
                            desc: 'Crafting clarity and conviction — narratives that resonate with investors, partners, and markets.',
                        },
                    ].map((s, i) => (
                        <Reveal key={s.title} delay={i * 0.12} className="bg-ivory">
                            <div className="group h-full p-8 md:p-10 lg:p-12 transition-colors duration-500 hover:bg-aizome hover:text-ivory">
                                <div className="flex items-baseline justify-between">
                                    <span className="numeral text-5xl md:text-6xl group-hover:text-ivory/40 transition-colors duration-500">
                                        {s.num}
                                    </span>
                                    <span className="font-jp text-2xl text-ink-soft group-hover:text-ivory/60 transition-colors duration-500">
                                        {s.jp}
                                    </span>
                                </div>
                                <h3 className="mt-12 font-serif text-3xl md:text-4xl tracking-tight">
                                    {s.title}
                                </h3>
                                <p className="mt-5 text-ink-soft group-hover:text-ivory/80 leading-relaxed font-light text-[15px] transition-colors duration-500 max-w-[36ch]">
                                    {s.desc}
                                </p>
                                <Link
                                    to="/services"
                                    data-testid={`home-pillar-${s.title.toLowerCase().replace(' ', '-')}`}
                                    className="mt-12 inline-flex items-center gap-3 overline text-ink group-hover:text-ivory transition-colors duration-500"
                                >
                                    Explore <ArrowRight size={14} strokeWidth={1} />
                                </Link>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* PHILOSOPHY snippet */}
            <section
                aria-label="Philosophy"
                className="px-6 md:px-12 lg:px-24 py-24 lg:py-32 border-t border-ink/10"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <Reveal>
                            <p className="overline">— 03 / Philosophy</p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <h2 className="mt-6 font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                                Make the<br />
                                <em className="italic font-light">PIE bigger.</em>
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6 grid grid-cols-1 md:grid-cols-3">
                        {[
                            { letter: 'P', word: 'Partnership', body: 'walk alongside clients, fostering trust and shared purpose.' },
                            { letter: 'I', word: 'Integrity', body: 'deliver honest, independent advice grounded in ethics.' },
                            { letter: 'E', word: 'Execution', body: 'bridge ideas and impact, accelerating implementation.' },
                        ].map((p, i) => (
                            <Reveal
                                key={p.letter}
                                delay={0.2 + i * 0.12}
                                className={`py-8 md:py-0 md:px-6 ${i !== 0 ? 'border-t md:border-t-0 md:border-l border-ink/15' : ''}`}
                            >
                                <p className="numeral text-7xl md:text-8xl leading-none">{p.letter}</p>
                                <h3 className="mt-6 font-serif text-2xl tracking-tight">{p.word}</h3>
                                <p className="mt-3 text-ink-soft text-[14px] leading-relaxed font-light">
                                    {p.body}
                                </p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FUJI BOTTOM CTA */}
            <section
                aria-label="Begin the journey"
                className="relative h-[80vh] min-h-[560px] flex items-center justify-center overflow-hidden border-t border-ink/10"
            >
                <motion.img
                    src={FUJI_INDIGO}
                    alt="Mount Fuji at dawn"
                    className="absolute inset-0 w-full h-full object-cover"
                    initial={{ scale: 1.08 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
                />
                <div className="absolute inset-0 bg-aizome/70" />
                <FujiLines className="absolute bottom-0 left-0 right-0 w-full h-40 text-ivory/15" stroke="rgba(253, 251, 247, 0.25)" />

                <div className="relative z-10 text-center px-6 max-w-4xl">
                    <Reveal>
                        <p className="overline text-ivory/70">— Begin the Journey</p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <h2 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl text-ivory tracking-tight leading-[0.95]">
                            Every great enterprise<br />
                            <em className="italic font-light">begins with a step.</em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.35}>
                        <Link to="/contact" data-testid="home-final-cta" className="btn-light mt-12">
                            Walk With Us <span className="arrow">→</span>
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default Home;
